import React, { useState } from 'react';
import { AppState } from '../../store/state';
import { AppDispatch } from '../../store/store';
import { connect } from 'react-redux';
import { selectFormState, selectPaperStatusState } from '../../store/selectors';
import {
  CategoryEnum,
  CategoryType,
  PaperStatus,
  UpdatePaperStatusFormRequest,
} from '../../store/paper-status/types.d';
import Loading from '../../components/Loading/Loading';
import { AddButton } from '../../components/ui/inputs/AddButton/AddButton';
import PaperStatusDialog from '../../components/dialogs/PaperStatusDialog/PaperStatusDialog';
import TrashCan from '../../icons/TrashCan';
import EditIcon from '../../icons/Edit';
import styled from 'styled-components';
import SimpleDialog from '../../components/dialogs/SimpleDialog/SimpleDialog';
import paperStatusSlice from '../../store/paper-status/slice';
import useTheme from '@material-ui/core/styles/useTheme';
import { useTranslation } from 'react-i18next';
import { getPaperStatusName } from '../../helpers/translations';
import { HowItWorksButton } from '../../components/ui/inputs/HowItWorksButton/HowItWorksButton';
import ViewNewIcon from '../../icons/ViewNew';
import { Form } from '../../store/form/types';
import PaperStatusFormDialog from '../../components/dialogs/PaperStatusFormDialog/PaperStatusFormDialog';
import DocumentationSidePanel from '../../components/ui/utils/DocumentationSidePanel/DocumentationSidePanel';

interface ButtonProps {
  onClick: () => void;
  disabled: boolean;
}

const Button: React.FC<ButtonProps> = ({ onClick, disabled, children }) => {
  return (
    <div className="border-l-2 border-white border-solid">
      <button
        className={`bg-transparent p-2.5 flex items-center ${disabled ? 'pointer-events-none opacity-20' : ''}`}
        onClick={onClick}
      >
        {children}
      </button>
    </div>
  );
};

interface Props {
  loading: boolean;
  paperStatusById: { [key: number]: PaperStatus };
  deletePaperStatusAction: (id: number) => void;
  formById: { [key: number]: Form };
  updatePaperStatusFormAction: (data: UpdatePaperStatusFormRequest) => void;
}

const SettingsPaperStatusPage: React.FC<Props> = ({
  loading,
  paperStatusById,
  deletePaperStatusAction,
  formById,
  updatePaperStatusFormAction,
}) => {
  const { t, i18n } = useTranslation();
  const [dialog, setDialog] = useState<JSX.Element | undefined>(undefined);
  const theme = useTheme();

  const formatCategory = (category: CategoryType): string => {
    let ret = '';
    switch (category) {
      case CategoryEnum.In_Progress:
        ret = t('In progress');
        break;
      case CategoryEnum.Rejection:
        ret = t('Rejection');
        break;
      case CategoryEnum.Accepted:
        ret = t('Accepted');
        break;
    }
    return ret;
  };

  if (loading) return <Loading />;

  return (
    <div className="w-full flex" style={{ flexGrow: 1 }}>
      <div className="w-full">
        <div className="text-sm w-full max-w-4xl">
          {Object.values(CategoryEnum).map((category, index) => {
            const formatted = formatCategory(category);
            return (
              <div key={index} className="text-sm w-full max-w-screen-2xl mb-14">
                <div className="flex mb-8 items-center">
                  <h2 className="font-bold text-xl mr-8">{formatted}</h2>
                  <AddButton
                    label={t('Add {{name}} status', { name: formatted })}
                    onClick={() =>
                      setDialog(
                        <PaperStatusDialog
                          open={true}
                          title={t('Add {{name}} status', { name: formatted })}
                          handleClose={() => setDialog(undefined)}
                          initialValue={{
                            id: 0,
                            name: '',
                            category: category,
                            display_name: category === CategoryEnum.In_Progress ? t('In the review process') : '',
                          }}
                        />,
                      )
                    }
                  />
                  {category == CategoryEnum.In_Progress && (
                    <HowItWorksButton src="https://embed.app.guidde.com/playbooks/9KzCbRKipEVPLVE2THHpVR" />
                  )}
                </div>
                <ul className="paperStatusListWrapper">
                  {Object.values(paperStatusById)
                    .filter((paperStatus) => paperStatus.category == category)
                    .map((paperStatus) => {
                      const formattedName = getPaperStatusName(paperStatus.name, t);
                      const isEditDisabled = ['Withdrawn', 'Deleted'].includes(paperStatus.name);
                      const isDeleteDisabled = ['Withdrawn', 'Deleted', 'Awaiting decision'].includes(paperStatus.name);
                      return (
                        <StyledPaperStatus key={paperStatus.id}>
                          <div className="flex w-full justify-between bg-transparent mb-2">
                            <div className="formattedNameWrapper flex w-full py-2 px-4">
                              <span>{formattedName}</span>
                              {paperStatus.display_name && (
                                <span>
                                  <span className="ml-1.5 mr-1.5">|</span>
                                  <span className="font-medium text-gray-400" style={{ fontSize: '0.8125rem' }}>
                                    {t('Display name: {{name}}', { name: paperStatus.display_name })}
                                  </span>
                                </span>
                              )}
                            </div>
                            <Button
                              onClick={() =>
                                setDialog(
                                  <PaperStatusFormDialog
                                    paperStatus={paperStatus}
                                    formById={formById}
                                    handleClose={() => setDialog(undefined)}
                                    handleOk={(formIds) =>
                                      updatePaperStatusFormAction({
                                        paper_status_id: paperStatus.id,
                                        form_ids: formIds,
                                      })
                                    }
                                  />,
                                )
                              }
                              disabled={false}
                            >
                              <ViewNewIcon color={theme.palette.text.primary} className="-mt-0.5 inline-block" />
                              <span className="ml-1">{t('Visibility')}</span>
                            </Button>
                            <Button
                              onClick={() =>
                                setDialog(
                                  <PaperStatusDialog
                                    open={true}
                                    title={t('Edit {{name}} status', { name: formattedName })}
                                    handleClose={() => setDialog(undefined)}
                                    initialValue={paperStatus}
                                  />,
                                )
                              }
                              disabled={isEditDisabled}
                            >
                              <EditIcon color={theme.palette.text.primary} className="-mt-0.5 inline-block" />
                              <span className="ml-1">{t('Edit')}</span>
                            </Button>
                            <Button
                              onClick={() => {
                                setDialog(
                                  <SimpleDialog
                                    open={true}
                                    handleClose={() => setDialog(undefined)}
                                    handleOk={() => deletePaperStatusAction(paperStatus.id)}
                                    title={t('Remove paper status?')}
                                  >
                                    <p className="text-sm mb-2">
                                      {t(
                                        `Are you sure you want to remove paper status {{name}}? Take into account that this action can't be undone.`,
                                        { name: formattedName },
                                      )}
                                    </p>
                                  </SimpleDialog>,
                                );
                              }}
                              disabled={isDeleteDisabled}
                            >
                              <TrashCan /> <span className="ml-1">{t('Delete')}</span>
                            </Button>
                          </div>
                        </StyledPaperStatus>
                      );
                    })}
                </ul>
              </div>
            );
          })}
          {dialog}
        </div>
      </div>

      <DocumentationSidePanel>
        <h3>Lorem ipsum dolor sit amet consectetur in arcu metus.</h3>
        <p>
          Lorem ipsum dolor sit amet consectetur. Risus mi ultrices dolor id tellus mi tellus. Etiam velit mi in hac
          nunc cras venenatis rhoncus. In cras facilisi feugiat sed nibh posuere sollicitudin faucibus sit. Netus amet
          blandit curabitur arcu. Lobortis ridiculus sapien augue quis amet faucibus.{' '}
        </p>
        <p>
          Nibh sapien sit diam aliquam eu adipiscing viverra faucibus at. Placerat congue pellentesque lobortis gravida.
          Ut aliquet sit mattis pharetra pellentesque hendrerit euismod tortor. Natoque ipsum adipiscing ultricies
          volutpat ultrices lectus maecenas malesuada. Pulvinar vel ac et velit. Adipiscing est bibendum quis quam.
        </p>
        <p>
          Volutpat ultrices eget eleifend scelerisque donec id id tristique. Et laoreet et enim ut. Volutpat purus nunc
          ipsum nisl bibendum id suspendisse ridiculus cursus.
        </p>
      </DocumentationSidePanel>
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectPaperStatusState(state).loading,
  paperStatusById: selectPaperStatusState(state).paperStatusById,
  formById: selectFormState(state).formById,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  deletePaperStatusAction: (id: number) => dispatch(paperStatusSlice.actions['DELETE:PAPER_STATUS'](id)),
  updatePaperStatusFormAction: (data: UpdatePaperStatusFormRequest) =>
    dispatch(paperStatusSlice.actions['PUT:PAPER_STATUS_FORM'](data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsPaperStatusPage);

const StyledPaperStatus = styled.li`
  > div {
    /*
    margin-bottom: 0.5rem;
    width: max-content;
    padding: 0.5rem 1rem;
    background-color: rgba(229, 231, 235, var(--tw-bg-opacity));
    border-radius: 18px;
    */
    background-color: #e3e5e6;
  }
`;
